// How a web push is shown — one rule, used by the service worker (sw/sw.ts) to
// decide and by the app shell (components/PushNoticeListener.tsx) to show it.
//
// Issue 272. A push that arrives while the app is open and in front shows as an
// in-app notice (a toast with a link to what it is about) instead of a system
// notification, on the browsers that allow it. Everywhere else, and whenever
// the page does not confirm in time that it showed the notice, the service
// worker shows the system notification, as it always did.
//
// "The browsers that allow it": every push subscription promises to show a
// notification for each push (userVisibleOnly). Chromium (Chrome, Edge,
// Samsung Internet, …) waives that while a window of the site is visible.
// Safari, the iPhone home-screen app included, does not: after three pushes
// without a system notification it cancels the subscription, and the device
// silently receives nothing until it is added again. Firefox counts such
// pushes against a quota. So only Chromium gets the in-app notice.
//
// Before this, the toast was attached by Firebase's onMessage() inside
// enableNotifications(), which ran only when someone pressed Enable — and even
// then it never fired: onMessage hears only messages that Firebase's own
// service-worker code posts, and this app's worker does not load that code.
//
// No imports, and nothing from window, React or the Firebase SDK: the service
// worker bundles this file.

/** The message type the service worker posts to a window. */
export const PUSH_NOTICE_MESSAGE = 'PUSH_NOTICE';

/**
 * The window must show a notice within this long of the worker sending it,
 * and the worker waits a little longer than that for the window's answer. A
 * window that answers in time is never also shown a system notification, and
 * one that is too late (busy, or about to unload) shows nothing, so the person
 * gets exactly one of the two.
 */
export const IN_APP_NOTICE_DEADLINE_MS = 1000;
export const IN_APP_NOTICE_WAIT_MS = 1500;

/** What a push says, read once for both ways of showing it. */
export interface PushNotice {
  title: string;
  body: string;
  /** An app path ("/tickets/abc") or another site's URL; null when it leads nowhere. */
  link: string | null;
}

/** A push as the service worker received it. */
export interface PushMessage {
  notice: PushNotice;
  /** The push's data fields, kept on a system notification for its click. */
  data: Record<string, string>;
  tag?: string;
}

/** What the worker posts to the window in front. */
export interface PushNoticeMessage {
  type: typeof PUSH_NOTICE_MESSAGE;
  notice: PushNotice;
  /** Date.now() after which the window must not show it: the worker has moved on. */
  deadline: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read a push payload: FCM's JSON, `{ notification: { title, body }, data }`.
 * It comes off the network, so every field is checked rather than trusted.
 */
export function readPush(payload: unknown): PushMessage {
  const p = isRecord(payload) ? payload : {};
  const notification = isRecord(p.notification) ? p.notification : {};
  // FCM data values are always strings (the Admin SDK rejects anything else).
  const data = isRecord(p.data) ? (p.data as Record<string, string>) : {};
  return {
    notice: {
      title: typeof notification.title === 'string' ? notification.title : 'OnLogic Team',
      body: typeof notification.body === 'string' ? notification.body : '',
      link: pushLink(data),
    },
    data,
    tag: typeof data.tag === 'string' && data.tag ? data.tag : undefined,
  };
}

/**
 * An in-app route ("/purchase-requests/PR-1"), as opposed to another site. A
 * leading "//" or "/\" is excluded: browsers read both as the start of another
 * host's address.
 */
export function isAppPath(link: string): boolean {
  return /^\/(?![/\\])/.test(link);
}

/**
 * Where a push leads: its `deeplink`, the field every server-side sender sets
 * (functions/src/notifications/sendNotification.ts), when it is an app path or
 * a web address — the same two kinds the notification inbox opens. Anything
 * else leads nowhere rather than somewhere unexpected.
 */
export function pushLink(data: unknown): string | null {
  const link = isRecord(data) ? data.deeplink : undefined;
  if (typeof link !== 'string') return null;
  if (isAppPath(link) || /^https?:\/\//i.test(link)) return link;
  return null;
}

/**
 * Whether this browser lets a push go without a system notification while the
 * site is in front. Chromium-based browsers do, and their user agent says
 * "Chrome/" followed by a version. On iPhone and iPad every browser runs on
 * Safari's engine, so they never qualify. An unknown browser gets the system
 * notification, which is always safe.
 */
export function inAppNoticeAllowed(userAgent: string): boolean {
  return /\bChrome\/\d/.test(userAgent) && !/\b(?:iPhone|iPad|iPod)\b/.test(userAgent);
}

/** The parts of a service worker's WindowClient the choice below reads. */
export interface WindowLike {
  url: string;
  focused: boolean;
  visibilityState: string;
}

/**
 * The app window the person is looking at, if any: ours, visible and focused.
 * A window that is merely visible, beside another program on a desktop, does
 * not count. Their attention is elsewhere, so a system notification is the way
 * to reach them.
 */
export function frontWindow<T extends WindowLike>(
  windows: readonly T[],
  origin: string,
): T | undefined {
  return windows.find(
    (w) => w.focused && w.visibilityState === 'visible' && new URL(w.url).origin === origin,
  );
}

/**
 * The worker's side: hand the notice to a window and wait for it to say it
 * showed it. Resolves false when no answer comes in time (nothing listening,
 * as on the sign-in page or in an old version of the page, or a page too busy
 * to answer), and the caller shows the system notification instead.
 */
export function offerPushNotice(
  target: { postMessage(message: unknown, transfer: Transferable[]): void },
  notice: PushNotice,
  now: number = Date.now(),
): Promise<boolean> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => finish(false), IN_APP_NOTICE_WAIT_MS);
    function finish(shown: boolean) {
      clearTimeout(timer);
      channel.port1.close();
      resolve(shown);
    }
    channel.port1.onmessage = (event: MessageEvent) =>
      finish(isRecord(event.data) && event.data.shown === true);
    const message: PushNoticeMessage = {
      type: PUSH_NOTICE_MESSAGE,
      notice,
      deadline: now + IN_APP_NOTICE_DEADLINE_MS,
    };
    target.postMessage(message, [channel.port2]);
  });
}

function isPushNoticeMessage(value: unknown): value is PushNoticeMessage {
  if (!isRecord(value) || value.type !== PUSH_NOTICE_MESSAGE) return false;
  const { notice, deadline } = value;
  return (
    typeof deadline === 'number' &&
    isRecord(notice) &&
    typeof notice.title === 'string' &&
    typeof notice.body === 'string' &&
    (notice.link === null || typeof notice.link === 'string')
  );
}

/**
 * The window's side: show a notice the worker sent and tell it so, which is
 * what stops it from also showing the system notification. Returns whether it
 * showed one. Anything else that arrives — another kind of message, or a
 * notice past its deadline — gets no answer.
 */
export function receivePushNotice(
  event: { data: unknown; ports: readonly MessagePort[] },
  show: (notice: PushNotice) => void,
  now: number = Date.now(),
): boolean {
  const message = event.data;
  const reply = event.ports[0];
  if (!isPushNoticeMessage(message) || !reply || now > message.deadline) return false;
  show(message.notice);
  reply.postMessage({ shown: true });
  return true;
}
